import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/Sidebar';
import usePizzerias from '../../hooks/usePizzerias';
import PaginationControls from '../../components/PaginationControls';
import { MdPowerSettingsNew } from 'react-icons/md';
import { FaPencilAlt, FaDownload } from 'react-icons/fa';
import { setPizzeriaStatus, exportPizzeriasToCSV } from '../../services/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CMS_ROUTES } from '../../../constants';
import Spinner from '../../components/Spinner';
import { useAuth } from '../../provider/auth-context';
import './PizzeriasListPage.css';

const PizzeriasListPage = () => {
  // Initialize from URL search params
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPage = parseInt(searchParams.get('page') || '1', 10);
  const initialLimit = parseInt(searchParams.get('limit') || '10', 10);
  const initialQuery = searchParams.get('query');
  const initialStatusParam = searchParams.get('status');
  const initialStatus = initialStatusParam === null ? null : initialStatusParam;

  const [page, setPage] = useState(initialPage);
  const [limit, setLimit] = useState(initialLimit);
  const [filters, setfilters] = useState({ query: initialQuery || null, status: initialStatus });
  const { pizzeriasData, loading: loadingPizzeria, error, setPizzeriasData } = usePizzerias(page, limit, filters);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  // Keep URL in sync with current pagination and filters
  useEffect(() => {
    const params = {};
    if (page) params.page = String(page);
    if (limit) params.limit = String(limit);
    if (filters.query) params.query = String(filters.query);
    if (filters.status !== null && filters.status !== undefined && filters.status !== '-1') {
      params.status = String(filters.status);
    }
    setSearchParams(params, { replace: true });
  }, [page, limit, filters, setSearchParams]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleLimitChange = (event) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleSearchClick = (filterData) => {
    setPage(1);
    const filterCopy = { ...filters };

    filterCopy.status = null;
    if (filterData.status !== '-1') {
      filterCopy.status = filterData.status;
    }
    filterCopy.query = null;
    if (filterData.query !== '') {
      filterCopy.query = filterData.query;
    }
    setfilters(filterCopy);
  };

  const changeStatus = async (id, active) => {
    try {
      setLoading(true);
      await setPizzeriaStatus(id, !active);
      const copy = { ...pizzeriasData };
      copy.data.find((pizzeria) => pizzeria.id === id).active = !active;
      setPizzeriasData(copy);
      setLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handleExportCSV = async () => {
    try {
      setLoading(true);
      const csvBlob = await exportPizzeriasToCSV();
      
      // Create download link
      const url = window.URL.createObjectURL(csvBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `pizzerias_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setLoading(false);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      setLoading(false);
    }
  };

  if (error) {
    return <p>Error: {error.message}</p>;
  }

  return (
    <div className="pizzerias-page cms">
      {loading && <Spinner />}
      <div className="main-content">
        <Sidebar />
        <div className="content">
          {loadingPizzeria && <Spinner />}
          {!loadingPizzeria && pizzeriasData && (
            <>
              <div className="d-flex justify-content-between align-items-center">
                <h2>Pizzerías</h2>
                {user?.admin && (
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={handleExportCSV}
                    disabled={loading}
                  >
                    <FaDownload className="me-2" />
                    Export to CSV
                  </button>
                )}
              </div>
              <hr />
              <PizzeriaSearchFilter
                onSearchClick={handleSearchClick}
                query={filters.query || ''}
                status={filters.status === null || filters.status === undefined ? '-1' : String(filters.status)}
              />
              <div className="pizzerias p-0 mt-5">
                Total: {pizzeriasData.total}
                <PizzeriaTable data={pizzeriasData.data} onStatusToggle={changeStatus} />
                <PaginationControls
                  total={pizzeriasData.total}
                  page={page}
                  limit={limit}
                  onPageChange={handlePageChange}
                  onLimitChange={handleLimitChange}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const PizzeriaSearchFilter = ({ onSearchClick, query: initialQuery = '', status: initialStatus = '-1' }) => {
  const [status, setStatus] = useState(initialStatus);
  const [query, setQuery] = useState(initialQuery);

  useEffect(() => {
    setQuery(initialQuery);
  }, [initialQuery]);

  useEffect(() => {
    setStatus(initialStatus);
  }, [initialStatus]);

  const handleSearch = () => {
    onSearchClick({ query, status });
  }

  const handleReset = () => {
    setQuery('');
    setStatus('-1');
    onSearchClick({ query: '', status: '-1' });
  }

  const handleStatusChange = (event) => {
    const newStatus = event.target.value;
    setStatus(newStatus);
    onSearchClick({ query, status: newStatus });
  };

  const handleQueryChange = (event) => {
    setQuery(event.target.value);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSearch();
    }
  };

  return (
    <>
      <div className="row">
        <div className="col">
          <label for="query">Nombre</label>
          <input
            type="text"
            placeholder="Buscar por nombre"
            value={query}
            onChange={handleQueryChange}
            onKeyDown={handleKeyDown}
            className="form-control"
          />
        </div>
        <div className="col">
          <label for="search">Estado</label>
          <select value={status} className="form-control" onChange={handleStatusChange} onKeyDown={handleKeyDown}>
            <option value="-1">Todas</option>
            <option value="active">Activa</option>
            <option value="accepted">Acepto</option>
            <option value="inactive">Inactiva</option>
          </select>
        </div>
      </div>
      <div className='row'>
        <div className='col d-flex justify-content-end gap-2'>
          <button className="btn btn-outline-secondary px-4" onClick={handleReset}>Limpiar</button>
          <button className="btn btn-custom-primary px-5" onClick={handleSearch}>Buscar</button>
        </div>
      </div>
    </>
  );
};

const PizzeriaTable = ({ data, onStatusToggle }) => {
  const navigate = useNavigate();
  return (
    <div className="table-responsive pb-2">
      <table className="table table-sm">
        <thead className="table-dark">
          <tr>
            <th scope="col">Id</th>
            <th scope="col">Nombre</th>
            <th scope="col">APYCE</th>
            <th scope="col">Dirección</th>
            <th scope="col">Tiene sucursales</th>
            <th scope="col">Activo</th>
            <th scope="col">Términos 2026</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {data.map((pizzeria) => (
            <tr key={pizzeria.id}>
              <td>{pizzeria.id}</td>
              <td>{pizzeria.fantasy_name}</td>
              <td>{pizzeria.apyce_member ? 'Si' : 'No'}</td>
              <td>
                <Tooltip text={pizzeria.address} maxLength={40} />
              </td>
              <td>{pizzeria.has_branches ? 'Si' : 'No'}</td>
              <td>{pizzeria.active ? 'Si' : 'No'}</td>
              <td>
                <span className={`badge ${pizzeria.accept_terms ? 'bg-success' : 'bg-warning'}`}>
                  {pizzeria.accept_terms ? 'Aceptado' : 'Pendiente'}
                </span>
              </td>
              <td>
                <div className="d-flex flex-row">
                  <ButtonTooltip icon={
                    <MdPowerSettingsNew
                      size={24}
                      color={pizzeria.active ? 'green' : 'red'}
                      style={{ 
                        cursor: pizzeria.accept_terms ? 'pointer' : 'not-allowed',
                        opacity: pizzeria.accept_terms ? 1 : 0.5
                      }}
                      onClick={pizzeria.accept_terms ? () => onStatusToggle(pizzeria.id, pizzeria.active) : undefined}
                      className="mx-2"
                    />
                  }
                    text={pizzeria.active ? 'Desactivar' : pizzeria.accept_terms ? 'Activar' : 'No aceptó los términos 2026'}
                  />
                  <ButtonTooltip icon={
                    <FaPencilAlt
                      size={24}
                      color='grey'
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`${CMS_ROUTES.EDIT_PIZZERIA}/${pizzeria.id}`)}
                      className="mx-2"
                    />
                  }
                    text="Editar"
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Tooltip = ({ text, maxLength }) => {
  const truncatedText = text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  return (
    <div className="custom-tooltip">
      {truncatedText}
      <span className="tooltiptext">{text}</span>
    </div>
  );
};

const ButtonTooltip = ({ icon, text }) => {
  return (
    <div className="button custom-tooltip">
      {icon}
      <span className="tooltiptext">{text}</span>
    </div>
  );
};

export default PizzeriasListPage;
